import { useTranslation } from 'react-i18next';
import { useUiStore } from '@/store/useUiStore';
import Reveal from '@/components/Reveal';
import './Ch5Impact.css';

interface Story {
  client: string;
  challenge: string;
  solution: string;
  metricValue: string;
  metricLabel: string;
  metric2Value?: string;
  metric2Label?: string;
}

export default function Ch5Impact() {
  const { t } = useTranslation();
  const setActiveChapter = useUiStore((s) => s.setActiveChapter);
  const stories = t('impact.stories', { returnObjects: true }) as Story[];
  const solutions = t('impact.solutions', { returnObjects: true }) as { name: string; desc: string }[];
  const clients = t('impact.clients', { returnObjects: true }) as string[];
  const sectors = t('impact.sectors', { returnObjects: true }) as string[];

  return (
    <section
      id="impact"
      className="chapter chapter-impact"
      aria-labelledby="impact-title"
      onMouseEnter={() => setActiveChapter('impact')}
    >
      <div className="chapter-inner">
        {/* 5a success stories */}
        <div className="chapter-head chapter-head--center">
          <p className="section-kicker">{t('impact.kicker')}</p>
          <h2 id="impact-title" className="chapter-title">
            {t('impact.storiesTitle')}
          </h2>
        </div>
        <div className="grid-3 impact-stories">
          {stories.map((s, i) => (
            <Reveal key={s.client} delay={i * 0.1}>
              <article className="card impact-story">
                <span className="impact-client">{s.client}</span>
                <div className="impact-metrics">
                  <div className="impact-metric">
                    <span className="impact-metric-val">{s.metricValue}</span>
                    <span className="impact-metric-label">{s.metricLabel}</span>
                  </div>
                  {s.metric2Value && (
                    <div className="impact-metric">
                      <span className="impact-metric-val">{s.metric2Value}</span>
                      <span className="impact-metric-label">{s.metric2Label}</span>
                    </div>
                  )}
                </div>
                <dl className="impact-cs">
                  <dt>›</dt>
                  <dd>{s.challenge}</dd>
                  <dt>✓</dt>
                  <dd>{s.solution}</dd>
                </dl>
              </article>
            </Reveal>
          ))}
        </div>

        {/* 5b delivered solutions wall */}
        <div className="impact-wall">
          <div className="chapter-head">
            <h3 className="chapter-title">{t('impact.wallTitle')}</h3>
            <p className="chapter-lead">{t('impact.wallLead')}</p>
          </div>
          <div className="wall-grid">
            {solutions.map((sol, i) => (
              <Reveal key={sol.name} delay={(i % 4) * 0.05}>
                <div className="wall-item">
                  <span className="wall-item-name">{sol.name}</span>
                  <span className="wall-item-desc">{sol.desc}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="wall-clients">
            <span className="wall-clients-label">{t('impact.clientsLabel')}</span>
            <div className="wall-clients-row">
              {clients.map((c) => (
                <span key={c} className="wall-client">
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* 5c sectors */}
        <div className="impact-sectors">
          <div className="chapter-head chapter-head--center">
            <h3 className="chapter-title">{t('impact.sectorsTitle')}</h3>
            <p className="chapter-lead">{t('impact.sectorsIntro')}</p>
          </div>
          <div className="sectors-grid">
            {sectors.map((s, i) => (
              <Reveal key={s} delay={(i % 6) * 0.04}>
                <div className="sector-tile">{s}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
